/**
 * PR-H — OSNAP aperture box.
 *
 * Small HTML square centred on the cursor when `OSNAPMODE > 0`, sized
 * by the AutoCAD `PICKBOX` sysvar (default 8 px). Acts as the visual
 * indicator of OSNAP capture range — when the user moves the mouse
 * inside the aperture, the snap engine treats nearby geometry as a
 * candidate snap target.
 *
 * # CSS pulse animation
 *
 * The box pulses at ~1.5 Hz to convey "active snap capture" state.
 * The animation is defined inline so the overlay drops in without a
 * stylesheet edit. Reduced-motion users get a static box (the pulse
 * is purely decorative — the snap engine itself does not depend on
 * frame timing).
 *
 * # Hidden during drag
 *
 * The host (Scene3DStage) passes `hidden=true` while a grip drag is
 * active so the aperture does not visually compete with the grip
 * marker the user is holding.
 *
 * # CSS keyframes pre-injection
 *
 * `@keyframes` rules cannot be inlined via `style={...}` — we inject
 * the keyframes once per page lifetime via a `<style>` tag managed by
 * a module-level boolean. This keeps the component self-contained.
 */

import * as React from "react";

const SNAP_APERTURE_KEYFRAME_ID = "scene3d-snap-aperture-keyframes";

/** PICKBOX sysvar default — 8 CSS pixels per AutoCAD docs. */
export const DEFAULT_PICKBOX_PX = 8;

function ensureKeyframesInjected(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(SNAP_APERTURE_KEYFRAME_ID)) return;
  const style = document.createElement("style");
  style.id = SNAP_APERTURE_KEYFRAME_ID;
  // The pulse alternates border opacity 1.0 → 0.45 → 1.0 over ~700 ms.
  // We avoid scaling the box itself so its hit-detection footprint
  // stays stable.
  style.textContent = `
    @keyframes scene3d-snap-aperture-pulse {
      0%   { border-color: rgba(241, 196, 15, 1); }
      50%  { border-color: rgba(241, 196, 15, 0.45); }
      100% { border-color: rgba(241, 196, 15, 1); }
    }
    @media (prefers-reduced-motion: reduce) {
      .scene3d-snap-aperture {
        animation: none !important;
      }
    }
  `;
  document.head.appendChild(style);
}

export interface SnapApertureBoxProps {
  /**
   * Cursor position in CSS pixels relative to the canvas's parent
   * element (the same element the rest of the HTML overlay layers
   * use). The box is centred on this point.
   */
  readonly cursorClient: { x: number; y: number } | null;
  /** PICKBOX sysvar value in CSS pixels. Default 8. */
  readonly pickboxPx?: number;
  /** Hide the box (drag in progress, OSNAPMODE = 0, etc.). */
  readonly hidden?: boolean;
}

/**
 * Pulsing HTML square indicating OSNAP capture range. Pure presentation
 * — no R3F dependencies, easy to mount under any HTML overlay layer.
 */
export function SnapApertureBox(
  props: SnapApertureBoxProps,
): React.ReactElement | null {
  const size = props.pickboxPx ?? DEFAULT_PICKBOX_PX;
  React.useEffect(() => {
    ensureKeyframesInjected();
  }, []);

  if (props.hidden) return null;
  if (!props.cursorClient) return null;

  return (
    <div
      data-testid="scene3d-snap-aperture-box"
      className="scene3d-snap-aperture"
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        width: size,
        height: size,
        boxSizing: "border-box",
        transform: `translate(${props.cursorClient.x - size / 2}px, ${
          props.cursorClient.y - size / 2
        }px)`,
        border: "1px solid rgba(241, 196, 15, 1)",
        borderRadius: 1,
        pointerEvents: "none",
        zIndex: 6,
        animation:
          "scene3d-snap-aperture-pulse 700ms ease-in-out infinite",
        background: "transparent",
      }}
    />
  );
}
